import CheckCircleOutlined from "@mui/icons-material/CheckCircleOutlined";
import { Alert, Box, Stack, TextField, Typography } from "@mui/material";
import { plural } from "../../program-access/access-model";
import { pathText } from "../../program-timeline/move-destinations";

import type { ProgramNode, ProgramTree } from "../../program-tree/program-tree";
import type { FC } from "react";

/**
 * The second step of a move: where the program goes, what moves with it, and a new name when the old
 * one is taken in the new place.
 */
export const MoveDetailsStep: FC<{
  tree: ProgramTree;
  program: ProgramNode;
  /** The new parent, or nothing for the top level. */
  parent: ProgramNode | undefined;
  /** The number of folders inside the program. */
  folders: number;
  /** The number of communications in the program and its folders, once they load. */
  communications: number | undefined;
  /** The name in the new place, as typed. Only a renamed program can change it. */
  name: string;
  renamed: boolean;
  taken: boolean;
  onName: (name: string) => void;
}> = ({ tree, program, parent, folders, communications, name, renamed, taken, onName }) => {
  const title = name.trim();

  return (
    <Stack spacing={2}>
      <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 2, rowGap: 0.5 }}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          From
        </Typography>
        <Typography variant="body2">{pathText(tree, program.id)}</Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          To
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {parent ? `${pathText(tree, parent.id)} › ` : ""}
          {title || program.title}
        </Typography>
      </Box>
      {communications === undefined ? null : (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {folders ? `${plural(folders, "folder")} and ` : ""}
          {plural(communications, "communication")} move with it.
        </Typography>
      )}
      {renamed ? (
        <Alert severity={taken ? "error" : "warning"}>
          <Stack spacing={1.5}>
            <span>
              {parent
                ? `${parent.title} already has a folder called “${program.title}”.`
                : `There’s already a top-level program called “${program.title}”.`}
              {taken && title ? ` “${title}” is taken too. Choose another name.` : ""}
              {taken && !title ? " Enter a name to continue." : ""}
              {taken ? "" : " We’ve suggested a new name. You can change it."}
            </span>
            <TextField
              label="New name"
              size="small"
              value={name}
              onChange={(event) => onName(event.target.value)}
              error={taken}
            />
          </Stack>
        </Alert>
      ) : null}
      <Stack component="ul" spacing={1} sx={{ m: 0, p: 0, listStyle: "none" }}>
        {[
          parent
            ? `It becomes a folder inside ${parent.title}, with everything inside it.`
            : "It becomes a top-level program, with everything inside it.",
          "Its colour and icon stay the same.",
          "Sending isn’t affected. Live and scheduled communications keep their timing and audiences.",
          "Everyone keeps the access they have today, with any exceptions on the folders inside.",
        ].map((fact) => (
          <Stack key={fact} component="li" direction="row" spacing={1}>
            <CheckCircleOutlined fontSize="small" color="success" />
            <Typography variant="body2">{fact}</Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};
